import React, { FC, useContext } from 'react';
import { VehiclePriceDetailsBlockProps } from '.';
import useTranslations from '@hooks/useTranslations';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { getModelTitle } from 'src/services/carList/utils/Car.utils';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST, FeatureSwitchContext } from 'src/context/featureSwitchApp';
import { usePrices } from '@hooks/usePriceCalculator';
import IconButton from '@components/IconButton';
import { getTotalOptionOrColorPrice } from '@utils/Price.utils';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { PaymentJourneyTypes } from 'src/partExchange/interfaces/Default';
import { checkDealRentingProductInAllowedRentingProducts } from '@utils/rentingProducts.utils';
import { useJourneyType } from '@hooks/useJourneyType';
import { useFinanceWidgetDuck } from '@hooks/useFinanceWidgetDuck';
import { useOptionPrice } from '@components/CarOptionsInterior/useOptionPrice';
import { isBrandAC, isBrandAP, isBrandDS, isBrandOV, isMarketGB, ModalVersion } from 'src/constants/main';
import { Icons } from '@components/Icon';
import { IConfiguratorOption } from 'src/redux/configurator/configurator.duck.interface';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { useDispatch } from 'react-redux';
import { useRenderDescription } from '@hooks/useRenderDescription';
import { IOption } from 'src/services/stock/models/stockItems.service.model';

const VehiclePriceDetailsBlockTemplate: FC<VehiclePriceDetailsBlockProps> = ({ className }) => {
    const { t } = useTranslations();
    const { renderDescription } = useRenderDescription();
    const { carDetails, currentDeal } = useCarDetailsDuck();
    const { financeQuote, pricesList } = useFinanceWidgetDuck();
    const { formatMoney, formatConfiguratorPrice } = useMoneyFormatter();
    const { cash } = usePrices(carDetails ?? null);
    const { basePrice } = cash;
    const { paymentJourneyType, isRent } = useJourneyType();
    const dispatch = useDispatch();
    const { getPrice } = useOptionPrice();
    const { hasSelectedPaymentJourney = false } = currentDeal?.extraFields ?? {};
    const rentingProductsFs = useContext(FeatureSwitchContext)?.FEATURE_SWITCH_RENTING_PRODUCTS ?? '';
    const isTiresLabellingEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_TIRES_LABELLING_ENABLED);

    const hasAllowedRentingProduct =
        paymentJourneyType === PaymentJourneyTypes.FINANCE &&
        hasSelectedPaymentJourney &&
        checkDealRentingProductInAllowedRentingProducts(
            rentingProductsFs,
            pricesList?.[0]?.financialType || financeQuote?.financialType
        );

    const handleOptionBtnClick = (option: IConfiguratorOption) => {
        dispatch(
            UIDuck.openModal({
                data: {
                    category: option?.category?.category || option?.category,
                    image: option.images[0]['url'],
                    description: renderDescription(option?.description),
                    title: option.title,
                    ...(isTiresLabellingEnabled &&
                        option?.tires && {
                            tiresSubtitle: t('technicalFeatures.tiresDetailModal.title'),
                            tires: option.tires?.data,
                            tiresDescription: t('technicalFeatures.tiresDetailModal.subtitle'),
                        }),
                },
                modalType: 'CAR_OPTION_DETAIL',
                modalVersion: ModalVersion.v2,
            })
        );
    };

    const renderOptions = () => (
        <div className="row">
            <div className="rowTitle">{t('summary.option.sectionTitle')}</div>
            <div className="wrapper">
                {carDetails?.options.map((option: any, index: number) => (
                    <div key={index} className="infoOptions">
                        <span className="txt">{option.title}</span>
                        <IconButton
                            className="infoIcon"
                            icon={
                                isBrandAC || isBrandAP || isBrandDS || (isMarketGB && isBrandOV)
                                    ? Icons.InfoCircle
                                    : Icons.Info
                            }
                            width={isBrandOV ? 16 : 14}
                            height={isBrandOV ? 16 : 14}
                            onClick={() => handleOptionBtnClick(option)}
                        />
                        {!hasAllowedRentingProduct && (
                            <div className="price" data-testid="TESTING_VEHICLE_SUMMARY_OPTION_PRICE">
                                {!isRent && !isDemoVehicle
                                    ? formatMoney(getTotalOptionOrColorPrice(option, paymentJourneyType), true)
                                    : ''}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    const priceTTC =
        carDetails.options.reduce(
            (sum: number, option: IOption) => sum + getTotalOptionOrColorPrice(option, paymentJourneyType),
            0
        ) +
        getPrice(carDetails.interiorColour).totalPrice +
        getPrice(carDetails.exteriorColour).totalPrice;

    const isDemoVehicle = carDetails?.mileage && carDetails?.registrationDate;

    const rows = [
        {
            titleKey: 'summary.vehicle.modelName',
            infoContent: (
                <>
                    <span>
                        {`${getModelTitle(carDetails, carDetails?.displayGrBodyStyleLabel)} - ${
                            carDetails?.specPack?.title
                        }`}
                    </span>
                    <span>
                        {`${carDetails?.fuel} - ${
                            carDetails?.gearbox?.specs.find(({ key }: any) => key === 'type')?.label
                        } ${
                            carDetails?.characteristics?.find(({ key }: any) => key === 'maximumPowerKW')?.value
                                ? `- ${
                                      carDetails?.characteristics?.find(({ key }: any) => key === 'maximumPowerKW')
                                          ?.value
                                  } ${t('configurator.info.power.label')}`
                                : ''
                        }`}
                    </span>
                </>
            ),
            priceValue: basePrice,
            dataTestId: 'TESTING_VEHICLE_SUMMARY_BASE_PRICE',
            formattedPrice: isDemoVehicle ? '' : formatMoney(basePrice, true),
        },
        {
            titleKey: 'summary.color.sectionTitle',
            infoContent: <span>{carDetails?.exteriorColour?.title}</span>,
            priceValue: getPrice(carDetails?.exteriorColour).totalPrice,
            dataTestId: 'TESTING_VEHICLE_SUMMARY_EXTERIOR_COLOR_PRICE',
            formattedPrice: isDemoVehicle
                ? ''
                : formatConfiguratorPrice(
                      getPrice(carDetails?.exteriorColour).totalPrice,
                      '+ ',
                      true,
                      t('configurator.colors.includedInPrice'),
                      null
                  ),
        },
        {
            titleKey: 'summary.interior.sectionTitle',
            infoContent: <span>{carDetails?.interiorColour?.title}</span>,
            priceValue: getPrice(carDetails?.interiorColour).totalPrice,
            dataTestId: 'TESTING_VEHICLE_SUMMARY_INTERIOR_COLOR_PRICE',
            formattedPrice: isDemoVehicle
                ? ''
                : formatConfiguratorPrice(
                      getPrice(carDetails?.interiorColour).totalPrice,
                      '+ ',
                      true,
                      t('configurator.colors.includedInPrice'),
                      null
                  ),
        },
        {
            optionsSection: carDetails.options?.length > 0 && renderOptions(),
        },
    ];

    return (
        <div className={className}>
            {rows.map(({ titleKey, infoContent, priceValue, dataTestId, formattedPrice, optionsSection }, index) => {
                if (optionsSection) {
                    return optionsSection;
                }

                return (
                    <div className="row" key={index}>
                        <span className="rowTitle">{t(titleKey)}</span>
                        <div className="info">
                            <div className="txt">{infoContent}</div>
                            {!hasAllowedRentingProduct && priceValue !== null && (
                                <div className="price" data-testid={dataTestId}>
                                    {!isRent ? formattedPrice : ''}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default VehiclePriceDetailsBlockTemplate;
